import React, { useState, useEffect, useRef } from 'react';
import { Image as ImageIcon, Maximize2, Loader2 } from 'lucide-react';

/**
 * ImageAttachment - Displays image attachments with preview
 * Handles loading signed URLs and fallback states
 */
const ImageAttachment = ({
    attachment,
    isMe = false,
    onPreview,
    filename,
    fileSize,
    formatFileSize,
    fetchSignedUrl // Custom function to fetch signed URL (e.g., ChatService.getAttachmentSignedUrl)
}) => {
    // Extract stable values from attachment immediately
    const directUrl = attachment?.signed_url ||
        attachment?.url ||
        attachment?.signedUrl ||
        attachment?.previewUrl ||
        attachment?.downloadUrl ||
        null;

    const objectKey = attachment?.objectKey ||
        attachment?.object_key ||
        attachment?.s3_key ||
        null;

    // Initialize with direct URL if available
    const [imageUrl, setImageUrl] = useState(directUrl);
    const [isLoading, setIsLoading] = useState(!directUrl);
    const [hasError, setHasError] = useState(false);

    // Use ref to track fetch status (persists without causing re-renders)
    const fetchedKeyRef = useRef(null);

    // Create a stable key for the attachment
    const attachmentKey = objectKey || directUrl || attachment?.id || filename;

    useEffect(() => {
        // Skip if we already have URL or already fetched this attachment
        if (imageUrl || fetchedKeyRef.current === attachmentKey) {
            setIsLoading(false);
            return;
        }

        // Skip if we have a direct URL (already set in initial state)
        if (directUrl) {
            setImageUrl(directUrl);
            setIsLoading(false);
            fetchedKeyRef.current = attachmentKey;
            return;
        }

        // Need to fetch signed URL
        if (!objectKey || !fetchSignedUrl) {
            setHasError(true);
            setIsLoading(false);
            fetchedKeyRef.current = attachmentKey;
            return;
        }

        let isMounted = true;
        fetchedKeyRef.current = attachmentKey; // Mark as fetching

        const loadImage = async () => {
            try {
                const result = await fetchSignedUrl(objectKey);
                const signedUrl = result?.signedUrl || result?.url || result?.data?.signedUrl || result?.data?.url;
                if (isMounted) {
                    if (signedUrl) {
                        setImageUrl(signedUrl);
                    } else {
                        setHasError(true);
                    }
                    setIsLoading(false);
                }
            } catch (err) {
                console.error('Failed to fetch signed URL for image:', err);
                if (isMounted) {
                    setHasError(true);
                    setIsLoading(false);
                }
            }
        };

        loadImage();

        return () => {
            isMounted = false;
        };
    // Only depend on attachmentKey for stability
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attachmentKey]);

    const handleImageError = () => {
        setHasError(true);
    };

    const handleClick = (e) => {
        e.preventDefault();
        if (onPreview) {
            onPreview(attachment);
        }
    };

    // Loading state (self-end ensures consistent right alignment)
    if (isLoading) {
        return (
            <div className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm border self-end ${isMe
                ? 'bg-sky-600/20 border-sky-500/30'
                : 'dark:bg-slate-900/80 bg-slate-100 dark:border-slate-700/60 border-slate-200'
            }`}>
                <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${isMe ? 'bg-sky-600/40' : 'dark:bg-slate-800 bg-white'}`}>
                    <Loader2 size={20} className="animate-spin text-sky-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="truncate font-semibold dark:text-slate-200 text-slate-700">{filename}</div>
                    <div className="text-xs text-slate-400">Loading preview...</div>
                </div>
            </div>
        );
    }

    // Error state / No URL available - show fallback (self-end ensures consistent right alignment)
    if (hasError || !imageUrl) {
        return (
            <button
                onClick={handleClick}
                className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm border transition text-left self-end ${isMe
                    ? 'bg-sky-600/20 border-sky-500/30 hover:bg-sky-600/30'
                    : 'dark:bg-slate-900/80 bg-slate-100 dark:border-slate-700/60 border-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800'
                }`}
            >
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${isMe ? 'bg-sky-600/40 text-white' : 'dark:bg-slate-800 bg-white dark:text-slate-300 text-slate-500'}`}>
                    <ImageIcon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="truncate font-semibold dark:text-slate-200 text-slate-700">{filename}</div>
                    <div className="text-xs text-slate-400">
                        image
                        {fileSize && formatFileSize ? ` • ${formatFileSize(fileSize)}` : ""}
                    </div>
                </div>
                <span className="text-xs font-semibold text-sky-500 dark:text-sky-300">View</span>
            </button>
        );
    }

    // Success - show image preview (self-end ensures consistent right alignment)
    return (
        <div
            className="relative group cursor-pointer self-end"
            onClick={handleClick}
        >
            <img
                src={imageUrl}
                alt={filename}
                className={`rounded-xl max-w-xs max-h-64 object-contain border-2 transition-all ${isMe
                    ? 'border-sky-500/30 hover:border-sky-400'
                    : 'dark:border-slate-700 border-slate-200 hover:border-sky-500/50'
                }`}
                loading="lazy"
                onError={handleImageError}
            />
            {/* Hover overlay - subtle indicator */}
            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <div className="bg-black/70 backdrop-blur-sm px-2 py-1 rounded-md text-xs font-medium text-white flex items-center gap-1.5 shadow-lg">
                    <Maximize2 size={12} />
                    View
                </div>
            </div>
            {/* Image info */}
            <div className={`mt-1 text-xs ${isMe ? 'text-sky-200/70' : 'text-slate-500'}`}>
                {filename}
                {fileSize && formatFileSize ? ` • ${formatFileSize(fileSize)}` : ""}
            </div>
        </div>
    );
};

export default ImageAttachment;
